import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, AlertTriangle, HelpCircle, Trash2, Clock,
  FileVideo, ExternalLink, ArrowRight, Link2, Search,
  Filter, Calendar, X, ChevronDown, Video, Image, Music,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ReportBadge } from "@/components/features/ReportBadge";
import { FilterDropdown } from "@/components/features/FilterDropdown";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_API_URL}/api`;

const VERDICT_META = {
  REAL:      { color: "text-verdict-real",      border: "border-verdict-real/30",      bg: "bg-verdict-real/5",      icon: ShieldCheck },
  FAKE:      { color: "text-verdict-fake",      border: "border-verdict-fake/30",      bg: "bg-verdict-fake/5",      icon: AlertTriangle },
  SUSPICIOUS:{ color: "text-verdict-uncertain",  border: "border-verdict-uncertain/30", bg: "bg-verdict-uncertain/5", icon: AlertTriangle },
  UNCERTAIN: { color: "text-verdict-uncertain",  border: "border-verdict-uncertain/30", bg: "bg-verdict-uncertain/5", icon: HelpCircle },
};

// ── Display label: risk-based (MVP Scope) ────────────────────────────────
function getDisplayLabel(verdict, confidence, t) {
  const v = (verdict || "UNCERTAIN").toUpperCase();
  const c = parseFloat(confidence) || 0;
  if (c < 60) return t("results.displayInsufficient"); // < 60% — insufficient
  if (c < 80) return t("results.displayUncertain");    // 60–79% — uncertain regardless
  if (v === "FAKE") return t("results.displayFake");   // ≥ 80% FAKE → HIGH RISK
  if (v === "REAL") return t("results.displayReal");   // ≥ 80% REAL → LOW RISK
  return t("results.displayUncertain");                // SUSPICIOUS / UNCERTAIN
}

const VERDICT_FILTER_OPTS = [
  { key: null,         labelKey: "history.verdictFilter.all" },
  { key: "FAKE",       labelKey: "history.verdictFilter.fake" },
  { key: "REAL",       labelKey: "history.verdictFilter.real" },
  { key: "UNCERTAIN",  labelKey: "history.verdictFilter.uncertain" },
];

const MEDIA_FILTER_OPTS = [
  { key: null,    labelKey: "history.mediaFilter.all",   Icon: Filter },
  { key: "video", labelKey: "history.mediaFilter.video", Icon: Video  },
  { key: "image", labelKey: "history.mediaFilter.image", Icon: Image  },
  { key: "audio", labelKey: "history.mediaFilter.audio", Icon: Music  },
];

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const item      = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

// ── DateRangePicker ─────────────────────────────────────────────────────────
function DateRangePicker({ dateFrom, dateTo, onChange, t }) {
  const [open, setOpen] = useState(false);
  const [localFrom, setLocalFrom] = useState(dateFrom || "");
  const [localTo,   setLocalTo]   = useState(dateTo   || "");
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const apply = () => { onChange(localFrom, localTo); setOpen(false); };
  const clear = () => { setLocalFrom(""); setLocalTo(""); onChange("", ""); setOpen(false); };
  const hasFilter = !!(dateFrom || dateTo);
  const label = hasFilter ? `${dateFrom || "…"} → ${dateTo || "…"}` : t("history.dateRange");

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="history-date-range-btn"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-mono transition-colors border ${
          hasFilter ? "bg-brand-primary text-white border-brand-primary"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
        }`}
      >
        <Calendar className="w-3 h-3" />
        <span>{label}</span>
        {hasFilter && (
          <X className="w-3 h-3 ml-0.5 hover:opacity-70" onClick={(e) => { e.stopPropagation(); clear(); }} />
        )}
        {!hasFilter && <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="absolute top-full mt-1 right-0 z-50 w-72 rounded-md border border-border bg-background shadow-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("history.dateRange")}</p>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("history.dateFrom")}</label>
              <input type="date" value={localFrom} onChange={(e) => setLocalFrom(e.target.value)}
                data-testid="history-date-from"
                className="w-full text-xs px-2 py-1.5 rounded-sm border border-border bg-secondary/30 text-foreground focus:outline-none focus:border-brand-primary" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("history.dateTo")}</label>
              <input type="date" value={localTo} onChange={(e) => setLocalTo(e.target.value)}
                data-testid="history-date-to"
                className="w-full text-xs px-2 py-1.5 rounded-sm border border-border bg-secondary/30 text-foreground focus:outline-none focus:border-brand-primary" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={apply} className="flex-1 h-7 rounded-sm text-xs bg-brand-primary text-white">{t("history.applyFilter")}</Button>
            <Button size="sm" variant="ghost" onClick={clear} className="h-7 rounded-sm text-xs">{t("history.reset")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function History() {
  const { t, i18n } = useTranslation();

  const [analyses, setAnalyses]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pages, setPages]         = useState(1);

  // Filters
  const [search,    setSearch]    = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [verdict,   setVerdict]   = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const debounceRef = useRef(null);

  const PAGE_SIZE = 15;

  const fetchData = useCallback(async (p = 1, opts = {}) => {
    setLoading(true);
    try {
      const lang = i18n.language?.split("-")[0] || "id";
      // Use opts value if key explicitly provided, else fall back to current state
      const v  = "verdict"   in opts ? opts.verdict   : verdict;
      const mt = "mediaType" in opts ? opts.mediaType : mediaType;
      const df = "dateFrom"  in opts ? opts.dateFrom  : dateFrom;
      const dt = "dateTo"    in opts ? opts.dateTo    : dateTo;
      const sq = "search"    in opts ? opts.search    : search;
      const params = {
        lang, page: p, page_size: PAGE_SIZE,
        ...(v  ? { verdict: v }     : {}),
        ...(mt ? { media_type: mt } : {}),
        ...(df ? { date_from: df }  : {}),
        ...(dt ? { date_to: dt }    : {}),
        ...(sq ? { search: sq }     : {}),
      };
      const res = await axios.get(`${API}/history`, { params, withCredentials: true });
      const data = res.data;
      setAnalyses(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPages(data.pages || 1);
    } catch {
      toast.error(t("history.loadError", "Gagal memuat riwayat"));
      setAnalyses([]);
    } finally {
      setLoading(false);
    }
  }, [i18n.language, verdict, mediaType, dateFrom, dateTo, search, t]);

  // Intentionally refresh on core filter/language changes only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(1); }, [verdict, mediaType, i18n.language]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    setSearch(q);
    fetchData(1, { search: q });
  };

  // Debounced search — fires 400ms after user stops typing
  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val.trim());
      fetchData(1, { search: val.trim() });
    }, 400);
  };

  const handleDateChange = (df, dt) => {
    setDateFrom(df); setDateTo(dt);
    fetchData(1, { dateFrom: df, dateTo: dt });
  };

  const handleVerdict = (v) => {
    setVerdict(v);
    fetchData(1, { verdict: v });
  };

  const handlePage = (p) => {
    fetchData(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id, name) => {
    try {
      await axios.delete(`${API}/history/${id}`, { withCredentials: true });
      setAnalyses((prev) => prev.filter((a) => a.id !== id));
      setTotal((t) => t - 1);
      toast.success(t("history.deleteSuccess", { name }));
    } catch {
      toast.error(t("history.deleteError", "Gagal menghapus"));
    }
  };

  const activeFilters = [verdict, mediaType, dateFrom, dateTo, search].filter(Boolean).length;

  const clearAll = () => {
    setVerdict(null); setMediaType(null); setDateFrom(""); setDateTo(""); setSearch(""); setSearchInput("");
    fetchData(1, { verdict: null, mediaType: null, dateFrom: "", dateTo: "", search: "" });
  };

  return (
    <main className="min-h-screen" data-testid="history-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">

        {/* Page Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("history.archive")}</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-2">{t("history.title")}</h1>
            {!loading && (
              <p className="text-muted-foreground mt-2 text-sm">
                <span className="font-mono text-brand-primary">{total.toLocaleString()}</span> {t("history.stored", { count: total }).replace(/^\d+ /, "")}
              </p>
            )}
          </div>
          <Link to="/analyze">
            <Button className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white gap-2" data-testid="new-analysis-btn">
              {t("nav.newAnalysis")} <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </motion.div>

        {/* ── Filter Bar ──────────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="history-filters">

          {/* Left: Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-0">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder={t("history.search.placeholder")}
                value={searchInput}
                onChange={handleSearchInput}
                data-testid="history-search-input"
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-sm border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand-primary"
              />
            </div>
            <Button type="submit" size="sm" variant="outline" className="rounded-sm h-7 px-3 text-xs shrink-0" data-testid="history-search-btn">
              {t("history.search.btn")}
            </Button>
          </form>

          {/* Right: Dropdowns + Date + Clear */}
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {/* Media type dropdown */}
            <FilterDropdown
              options={MEDIA_FILTER_OPTS}
              value={mediaType}
              onChange={(v) => { setMediaType(v); fetchData(1, { mediaType: v }); }}
              t={t}
              testId="history-media-dropdown"
            />

            {/* Verdict dropdown */}
            <FilterDropdown
              options={VERDICT_FILTER_OPTS}
              value={verdict}
              onChange={(v) => { setVerdict(v); fetchData(1, { verdict: v }); }}
              t={t}
              testId="history-verdict-dropdown"
            />

            {/* Date range */}
            <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} t={t} />

            {/* Clear all */}
            {activeFilters > 0 && (
              <button
                onClick={clearAll}
                data-testid="history-clear-filters"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-xs font-mono text-red-500 hover:bg-red-500/10 border border-red-500/20 transition-colors"
              >
                <X className="w-3 h-3" /> {t("history.resetCount", { count: activeFilters })}
              </button>
            )}
          </div>
        </motion.div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-36 rounded-md border border-border bg-card animate-pulse" />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && analyses.length === 0 && (
          <div className="text-center py-24 space-y-4" data-testid="history-empty">
            <FileVideo className="w-12 h-12 text-muted-foreground/30 mx-auto" />
            <div>
              <p className="font-heading text-xl font-semibold">{t("history.empty")}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("history.empty.desc")}</p>
            </div>
            {activeFilters > 0 ? (
              <Button variant="outline" className="rounded-md mt-2" onClick={clearAll}>{t("history.clearFilters")}</Button>
            ) : (
              <Link to="/analyze">
                <Button className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white mt-2" data-testid="history-start-btn">
                  {t("history.empty.btn")}
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Grid */}
        {!loading && analyses.length > 0 && (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${page}-${verdict}-${search}`}
              variants={container} initial="hidden" animate="show"
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              data-testid="history-grid"
            >
              {analyses.map((analysis) => {
                const meta = VERDICT_META[analysis.verdict] || VERDICT_META.UNCERTAIN;
                const Icon = meta.icon;
                const verdictLabel = getDisplayLabel(analysis.verdict, analysis.confidence, t);
                const date = analysis.created_at
                  ? new Date(analysis.created_at).toLocaleString(i18n.language === "en" ? "en-US" : "id-ID", {
                      timeZone: "Asia/Jakarta", month: "short", day: "numeric",
                      year: "numeric", hour: "2-digit", minute: "2-digit",
                    })
                  : "—";

                return (
                  <motion.div
                    key={analysis.id}
                    variants={item}
                    className="group rounded-md border border-border bg-card hover:border-brand-primary/20 transition-colors overflow-hidden"
                    data-testid={`history-card-${analysis.id}`}
                  >
                    {/* Thumbnail */}
                    {analysis.thumbnail ? (
                      <div className="relative h-28 bg-secondary/30 overflow-hidden">
                        <img src={analysis.thumbnail} alt="thumbnail"
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className={`absolute bottom-2 left-3 flex items-center gap-1.5 text-xs font-mono font-semibold px-2 py-0.5 rounded-sm ${meta.color} ${meta.border} border ${meta.bg}`}>
                          <Icon className="w-3 h-3" />{verdictLabel}
                        </div>
                        <span className={`absolute bottom-2 right-3 text-lg font-heading font-bold ${meta.color}`}>
                          {Math.round(analysis.confidence)}%
                        </span>
                      </div>
                    ) : (
                      <div className={`relative h-28 flex items-center justify-center ${meta.bg} border-b ${meta.border}`}>
                        <div className="text-center">
                          <Icon className={`w-8 h-8 mx-auto mb-1 ${meta.color} opacity-60`} />
                          <div className={`text-xs font-mono font-bold ${meta.color}`}>{verdictLabel}</div>
                          <div className={`text-lg font-heading font-bold ${meta.color}`}>{Math.round(analysis.confidence)}%</div>
                        </div>
                      </div>
                    )}

                    <div className="p-5">
                      {/* Title as link */}
                      <div className="mb-2">
                        {analysis.content_title ? (
                          <Link
                            to={`/results/${analysis.id}`}
                            state={{ result: analysis }}
                            className="text-sm font-semibold leading-snug line-clamp-1 mb-0.5 hover:text-brand-primary transition-colors block"
                            data-testid={`history-title-${analysis.id}`}
                          >
                            {analysis.content_title}
                          </Link>
                        ) : null}
                        <div className="flex items-center gap-1.5">
                          <FileVideo className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          <p className="text-xs font-mono text-muted-foreground truncate" data-testid={`history-name-${analysis.id}`}>
                            {analysis.video_name}
                          </p>
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground line-clamp-2 mb-3 leading-relaxed">
                        {analysis.excerpt || analysis.summary}
                      </p>

                      {analysis.video_url && (
                        <a href={analysis.video_url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs font-mono text-brand-primary hover:underline truncate max-w-full mb-3"
                          title={analysis.video_url} onClick={(e) => e.stopPropagation()}>
                          <Link2 className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{analysis.video_url}</span>
                        </a>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
                          <Clock className="w-3 h-3" />{date}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <ReportBadge result={analysis} />
                          <Link to={`/results/${analysis.id}`} state={{ result: analysis }}>
                            <Button size="sm" variant="ghost" className="h-7 px-2 rounded-sm text-xs gap-1"
                              data-testid={`view-result-${analysis.id}`}>
                              <ExternalLink className="w-3 h-3" /> {t("history.view")}
                            </Button>
                          </Link>
                          <Button size="sm" variant="ghost"
                            className="h-7 px-2 rounded-sm text-xs text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                            onClick={() => handleDelete(analysis.id, analysis.video_name)}
                            data-testid={`delete-analysis-${analysis.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10" data-testid="history-pagination">
            <Button variant="outline" size="sm" onClick={() => handlePage(page - 1)}
              disabled={page <= 1} className="rounded-sm text-xs">
              &larr; {t("history.prev")}
            </Button>
            <span className="text-xs font-mono text-muted-foreground px-2">{t("history.page", { page, pages })}</span>
            <Button variant="outline" size="sm" onClick={() => handlePage(page + 1)}
              disabled={page >= pages} className="rounded-sm text-xs">
              {t("history.next")} &rarr;
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
